import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  HStack,
  Input,
  Stack,
} from '@chakra-ui/react';
import { ClipboardListIcon } from '@heroicons/react/outline';
import axios from 'axios';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  CompanyCapitalType,
  DocumentStatus,
  FundraisingDocumentType,
  FundraisingStatus,
  SubscriptionWorkflowType,
  useGetFundraisingQuery,
} from '@blockpulse3/graphql/hooks';
import { useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';

type Props = unknown;

/**
 * FundraisingUploadCertificate.
 * Section to upload the deposit certificate and trigger the PV signature.
 *
 * @returns {JSX.Element | null}
 */
export function FundraisingUploadCertificate(): JSX.Element | null {
  const i18nFundraisingUploadCertificate = useTranslations('FundraisingUploadCertificate');

  const inputRef = useRef<HTMLInputElement>(null);

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { fundraisingId = '' } = useParams();

  const { data, loading, refetch } = useGetFundraisingQuery({ variables: { fundraisingId } });

  const [uploadLoading, setUploadLoading] = useState<boolean>(false);

  if (loading || !data) {
    return null;
  }

  const fundraising = data.fundraising;

  const isFixed = fundraising.company.capitalType === CompanyCapitalType.FIXED;
  const isAllIn = fundraising.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;
  const isClosed = fundraising.status === FundraisingStatus.CLOSED;
  const isReviewed = fundraising.status === FundraisingStatus.REVIEWED;

  const deposit = fundraising.documents?.find(
    (document) => document.type === FundraisingDocumentType.DEPOSIT_CERTIFICATE,
  );
  const isDepositSign = deposit ? deposit.document.status === DocumentStatus.SIGNED : false;

  const handleUploadButtonClick = (): void => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ): Promise<void> => {
    const files = e.target.files;

    if (files && files.length === 1) {
      const formData = new FormData();
      formData.append('fundraisingId', fundraisingId);
      formData.append('document', files[0]);
      setUploadLoading(true);

      await axios
        .post(
          process.env['NX_API_CONTROLLER_ENDPOINT'] + '/fundraisings/uploadDepositCertificate',
          formData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        .then(() => {
          refetch();
          successToast({ title: i18nFundraisingUploadCertificate('Toast.UploadSuccess') });
        })
        .catch(() => {
          errorToast({ title: i18nFundraisingUploadCertificate('Toast.UploadError') });
        })
        .finally(() => {
          setUploadLoading(false);
        });
    }
  };

  if (!isFixed || !((isAllIn && isReviewed) || (!isAllIn && isClosed)) || isDepositSign) {
    return null;
  }

  return (
    <Alert boxShadow="md" status="warning">
      <Stack
        alignItems="stretch"
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing="4"
        width="full"
      >
        <HStack alignItems="flex-start" spacing="0">
          <AlertIcon as={ClipboardListIcon} />
          <Stack spacing="0">
            <AlertTitle fontWeight="bold">
              {i18nFundraisingUploadCertificate('AlertTitle')}
            </AlertTitle>
            <AlertDescription>
              {i18nFundraisingUploadCertificate('AlertDescription')}
            </AlertDescription>
          </Stack>
        </HStack>
        <HStack alignItems="center" ml={{ base: '0', md: '4' }}>
          <Input
            accept=".pdf"
            display="none"
            ref={inputRef}
            type="file"
            onChange={handleInputChange}
          />
          <Button
            className="confirm"
            fontSize="sm"
            isLoading={uploadLoading}
            loadingText={i18nFundraisingUploadCertificate('ImportInProgress')}
            onClick={handleUploadButtonClick}
          >
            {i18nFundraisingUploadCertificate('ImportDepositCertificate')}
          </Button>
        </HStack>
      </Stack>
    </Alert>
  );
}

export type FundraisingUploadCertificateProps = Props;
